"use client";

import { type NewsletterTranslations } from "@/components/layout/footer/translations";
import DesktopHeader from "@/components/layout/header/desktop-header";
import MobileHeader from "@/components/layout/header/mobile-header";
import { type NavbarTranslations } from "@/components/layout/header/translations";
import { Locale, i18n } from "@/i18n/i18n.config";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

export interface PageProps {
  navigationEntries: HeaderNavigationEntry[];
  locale: Locale;
  region: string;
  navbarTranslations: NavbarTranslations;
  newsletterTranslations: NewsletterTranslations;
}

export default function Header({
  navigationEntries,
  locale,
  region,
  navbarTranslations,
  newsletterTranslations,
}: PageProps) {
  const SCROLL_HEIGHT_MOBILE = 100;
  const SCROLL_HEIGHT_DESKTOP = 105;

  const selectableLocales = i18n.locales.filter((availableLocale) => availableLocale != locale).sort();

  const [isBigHeaderShownOnMobile, setIsBigHeaderShownOnMobile] = useState(true);
  const [isBigHeaderShownOnDesktop, setIsBigHeaderShownOnDesktop] = useState(true);
  const router = useRouter();

  const handleScroll = () => {
    if (window.scrollY > SCROLL_HEIGHT_MOBILE) {
      setIsBigHeaderShownOnMobile(false);
    } else {
      setIsBigHeaderShownOnMobile(true);
    }

    if (window.scrollY > SCROLL_HEIGHT_DESKTOP) {
      setIsBigHeaderShownOnDesktop(false);
    } else {
      setIsBigHeaderShownOnDesktop(true);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLocaleChange = (selectedLocale: Locale) => {
    const currentPathname = window.location.pathname + window.location.search;
    const newPathname = `/${selectedLocale}${currentPathname.substring(locale.length + 1)}`;
    router.push(newPathname);
  };

  return (
    <>
      <DesktopHeader
        navigationEntries={navigationEntries}
        locale={locale}
        selectableLocales={selectableLocales}
        selectLocale={handleLocaleChange}
        isBigHeaderShown={isBigHeaderShownOnDesktop}
        region={region}
      />

      <MobileHeader
        navigationEntries={navigationEntries}
        locale={locale}
        selectableLocales={selectableLocales}
        selectLocale={handleLocaleChange}
        isBigHeaderShown={isBigHeaderShownOnMobile}
        navbarTranslations={navbarTranslations}
        newsletterTranslations={newsletterTranslations}
        region={region}
      />
    </>
  );
}

export type RawHeaderNavigationEntry = {
  label: string;
  url: string;

  description?: string;
  image?: string;

  subElements?: RawHeaderNavigationEntry[];
};

/** @deprecated Only here for compatibility, use RawHeaderNavigationEntry instead.  */
export type EntryWithImage = RawHeaderNavigationEntry;

/** @deprecated Only here for compatibility, use RawHeaderNavigationEntry instead.  */
export type EntryWithImageAndDescription = RawHeaderNavigationEntry;

/** @deprecated Only here for compatibility, use RawHeaderNavigationEntry instead.  */
export type HeaderNavigationEntry = RawHeaderNavigationEntry;

/** @deprecated Only here for compatibility, use RawHeaderNavigationEntry instead.  */
export type HeaderNavigationSubEntry = RawHeaderNavigationEntry;

/** @deprecated Only here for compatibility, use RawHeaderNavigationEntry instead.  */
export type HeaderNavigationSecondLevelEntry = RawHeaderNavigationEntry;

/** @deprecated Only here for compatibility, use RawHeaderNavigationEntry instead.  */
export type HeaderNavigationThirdLevelEntry = RawHeaderNavigationEntry;
